import { Flex } from '@chakra-ui/react';

import { formatNumber } from '@enterprise/core';

import type { PercentCellProps } from './types/percent-cell-renderer.params';

/**
 * Renders a percentage value for a cell, formatting it according to specified options.
 *
 * - If the cell is a footer, calculates the percentage as the ratio of the sum of `totalFields` to the sum of
 * `grandTotalFields` across all non-group nodes.
 * - Otherwise, uses the provided `value` as the percentage.
 * - Formats the percentage using `formatNumber` with up to 3 decimal places and at least 1 decimal place,
 * styled as a percent.
 *
 * @template TData - The type of the row data.
 * @template TContext - The type of the context.
 * @param props - The properties for the percent cell renderer.
 * @param props.api - The grid API for iterating over nodes.
 * @param props.grandTotalFields - The fields to sum for the grand total calculation.
 * @param props.node - The current node, used to determine if it's a footer.
 * @param props.totalFields - The fields to sum for the total calculation.
 * @param props.value - The percentage value to display if not a footer.
 * @returns The formatted percentage string.
 */
function PercentCellRenderer<TData = Record<string, unknown>, TContext = unknown>({
  api,
  grandTotalFields = [],
  maximumDecimals = 3,
  node,
  totalFields = [],
  value,
}: PercentCellProps<TData, TContext>) {
  let percentage = 0;

  if (node.footer) {
    let total = 0;
    let grandTotal = 0;

    api.forEachNode(({ data, group }) => {
      if (!(group ?? false)) {
        grandTotal = grandTotalFields.reduce((acc, field) => {
          return acc + ((data?.[field] as number | undefined) ?? 0);
        }, grandTotal);

        total = totalFields.reduce((acc, field) => {
          return acc + ((data?.[field] as number | undefined) ?? 0);
        }, total);
      }
    });

    percentage = grandTotal === 0 ? 0 : total / grandTotal;
  } else {
    percentage = value ?? 0;
  }

  const formatted = formatNumber(percentage, {
    settings: {
      maximumFractionDigits: maximumDecimals,
      minimumFractionDigits: Math.min(1, maximumDecimals),
    },
    style: 'percent',
  });

  return (
    <Flex
      grow={1}
      justifyContent="flex-end">
      {formatted}
    </Flex>
  );
}

export default PercentCellRenderer;
